import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuote } from "@/context/QuoteContext";

export const TagBadge = ({ tag, slug, className = "" }) => {
  if (!tag) return null;
  const isHot = tag === "Top Selling";
  return (
    <span
      data-testid={`package-tag-${slug}`}
      className={`tag-shine relative inline-block -rotate-3 rounded-full px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest shadow-lg ring-2 ring-white/70 ${
        isHot ? "bg-[#D99B26] text-[#0F2F24]" : "bg-[#0F2F24] text-[#D99B26]"
      } ${className}`}
    >
      {tag}
    </span>
  );
};

export const PriceTag = ({ price, className = "" }) => (  <div className={className} data-testid="package-price">
    {price ? (
      <>
        <span className="text-xs text-slate-500 block">Starting from</span>
        <span className="text-xl font-bold text-[#0F2F24]">₹{price.toLocaleString("en-IN")}</span>
        <span className="text-xs text-slate-500"> /person</span>
      </>
    ) : (
      <>
        <span className="text-xs text-slate-500 block">Starting from</span>
        <span className="text-base font-bold text-[#C28519]">Price on Request</span>
      </>
    )}
  </div>
);

const PackageCard = ({ pkg }) => {
  const { openQuote } = useQuote();
  return (
    <motion.article whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className="group flex flex-col overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-[#0F2F24]/8 hover:shadow-lg" data-testid={`package-card-${pkg.slug}`}>
      <Link to={`/packages/${pkg.slug}`} className="relative block aspect-[16/10] overflow-hidden">
        <img src={pkg.image} alt={`${pkg.name} - Kashmir holiday package`} loading="lazy"
          className={`h-full w-full transition-transform duration-500 group-hover:scale-105 ${pkg.image_fit === "contain" ? "object-contain bg-white" : "object-cover"}`} />
        <span className="absolute left-3 top-3 rounded-full bg-[#0F2F24]/90 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
          {pkg.duration}
        </span>
        <TagBadge tag={pkg.tag} slug={pkg.slug} className="!absolute right-3 top-3" />
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-serif text-xl font-semibold text-[#0F2F24]">
          <Link to={`/packages/${pkg.slug}`} className="hover:text-[#C28519] transition-colors" data-testid={`package-card-view-details-button-${pkg.slug}`}>
            {pkg.name}
          </Link>
        </h3>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-[#C28519]">
          <Clock className="h-3.5 w-3.5" /> {pkg.route.join(" → ")}
        </p>
        <p className="mt-3 line-clamp-3 text-sm text-slate-600 leading-relaxed">{pkg.description}</p>
        <div className="mt-4 flex items-end justify-between border-t border-slate-100 pt-4">
          <PriceTag price={pkg.price} />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild className="border-[#0F2F24] text-[#0F2F24] hover:bg-[#0F2F24] hover:text-white">
              <Link to={`/packages/${pkg.slug}`} data-testid={`package-card-view-button-${pkg.slug}`}>View Package</Link>
            </Button>
            <Button size="sm" onClick={() => openQuote(pkg.name)} data-testid={`package-card-quote-button-${pkg.slug}`}
              className="bg-[#D99B26] text-[#0F2F24] hover:bg-[#c98f22]">
              Get Quote <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default PackageCard;
